import { useCallback, useRef, useState, forwardRef } from 'react';
import styled from '@emotion/styled';
import Overlay from 'react-bootstrap/Overlay';
import BootstrapTooltip from 'react-bootstrap/Tooltip';
import { shadowLight } from 'app/colors';
import { breakpoints } from 'app/spacing';
import KizenTypography from 'app/kizentypo';
import { layers } from 'app/spacing';
import { markdownPreviewStyles } from 'components/WYSIWYG/MarkdownEditor/styles';

export const ICON_BUTTON_DELAY = 500;

const BaseTooltip = styled(BootstrapTooltip)`
  pointer-events: none; // When it overlaps the target, don't let loss of hover cause flicker
  padding: 6px 0;
  &.show {
    opacity: 1;
    z-index: ${layers.toast};
  }

  @media (max-width: ${breakpoints.sm}px) {
    width: auto;
    max-width: 99%;
  }

  .tooltip-inner {
    padding: 10px;
    background-color: var(--color-background-darker);
    border-radius: 4px;
  }
  .arrow {
    width: 12px;
    height: 6px;
    &:before {
      border-top-color: var(--color-border-darker);
      border-width: 6px 6px 0 6px;
    }
  }
  &.bs-tooltip-top .arrow:before {
    border-top-color: var(--color-border-darker);
    border-width: 6px 6px 0 6px;
  }
  &.bs-tooltip-bottom .arrow:before {
    border-bottom-color: var(--color-border-darker);
    border-width: 0 6px 6px 6px;
  }

  // reset sizes and reset border top color
  &.bs-tooltip-right,
  &.bs-tooltip-left {
    padding: 0 6px;
    .arrow {
      width: 6px;
      height: 12px;
    }
    .arrow:before {
      border-top-color: transparent;
    }
  }

  &.bs-tooltip-left .arrow:before {
    border-left-color: var(--color-border-darker);
    border-width: 6px 0 6px 6px;
  }
  &.bs-tooltip-right .arrow:before {
    border-right-color: var(--color-border-darker);
    border-width: 6px 6px 6px 0;
  }
`;

const Tooltip = forwardRef(({ label = null, children, ...others }, ref) => {
  return (
    <BaseTooltip ref={ref} {...others}>
      {label && (
        <KizenTypography size="small" color="var(--color-font-white)">
          {label}
        </KizenTypography>
      )}
      {children}
    </BaseTooltip>
  );
});

Tooltip.displayName = 'Tooltip';

export default Tooltip;

const HTMLTooltip = forwardRef(({ html = null, children, ...others }, ref) => {
  return (
    <BaseTooltip ref={ref} {...others}>
      {html && (
        <KizenTypography
          size="small"
          color="var(--color-font-white)"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      )}
      {children}
    </BaseTooltip>
  );
});

HTMLTooltip.displayName = 'HTMLTooltip';

const defaultPopperConfig = {
  modifiers: {
    preventOverflow: {
      enabled: true,
      boundariesElement: 'viewport',
    },
  },
};

export const OverlayTooltip = styled(Tooltip)`
  .tooltip-inner {
    max-width: ${(props) => props.maxWidth || 500}px;
    ${(props) =>
      props && props.tooltipInner && props.tooltipInner.disableMediaWidth
        ? ''
        : `
        @media (max-width: ${Math.round(500 / 0.9)}px) {
          max-width: 90%;
        }
      `}
    margin: 0 auto;
    display: flex;
    max-height: calc(4 * 1.1em + 2 * 10px); // 4 lines + padding
    ${shadowLight}
    > * {
      overflow: hidden;
      text-overflow: ellipsis;
      -webkit-line-clamp: 4;
      -webkit-box-orient: vertical;
      display: -webkit-box;
      white-space: pre-line;
    }
  }
`;

const OverlayHTMLTooltip = styled(HTMLTooltip)`
  .tooltip-inner {
    max-width: ${(props) => props.maxWidth || 500}px;

    @media (max-width: ${Math.round(500 / 0.9)}px) {
      max-width: 90%;
    }

    text-align: left;
    ${markdownPreviewStyles({ color: 'var(--color-font-white)' })}

    & p {
      margin: 0;
    }
  }
`;

const defaultPredicate = () => true;

export const useTooltip = function useTooltip(
  {
    tooltipLabel = '',
    delay = 0,
    label = '',
    html = null,
    popperConfig = {},
    description = undefined,
    showDescriptionOnPredicate = false,
    ...props
  } = {},
  predicate = defaultPredicate
) {
  const [tooltipInfo, setTooltipInfo] = useState(null);
  const timer = useRef(null);
  const hiddenRef = useRef(true);

  const handleOn = useCallback(
    (ev) => {
      hiddenRef.current = false;
      const { currentTarget: el } = ev;
      if (el) {
        timer.current = setTimeout(() => {
          if (!hiddenRef.current) {
            const showTooltip = predicate(el) || predicate(el.lastChild);
            if (
              tooltipLabel ||
              (description && !showDescriptionOnPredicate) ||
              showTooltip
            ) {
              const labelBase = showTooltip
                ? tooltipLabel
                  ? `${tooltipLabel}: `
                  : ''
                : tooltipLabel;
              const label =
                html === true
                  ? el.innerHTML
                  : description
                    ? description
                    : `${labelBase}${
                        showTooltip ? el.value || el.textContent : ''
                      }`;
              setTooltipInfo({
                target: el,
                label: label,
              });
            }
          }
        }, delay);
      }
    },
    [
      delay,
      predicate,
      tooltipLabel,
      description,
      showDescriptionOnPredicate,
      html,
    ]
  );

  const handleOff = useCallback(() => {
    hiddenRef.current = true;
    setTooltipInfo(null);
    clearTimeout(timer.current);
  }, []);

  return [
    {
      onFocus: handleOn,
      onBlur: handleOff,
      onMouseEnter: handleOn,
      onMouseLeave: handleOff,
    },
    tooltipInfo && (
      <Overlay
        show
        placement="top"
        target={tooltipInfo.target}
        {...props}
        popperConfig={{ ...defaultPopperConfig, ...popperConfig }}
      >
        {html ? (
          <OverlayHTMLTooltip
            html={html === true ? tooltipInfo.label : html}
            {...props}
          />
        ) : (
          <OverlayTooltip label={label || tooltipInfo.label} {...props} />
        )}
      </Overlay>
    ),
    handleOff,
  ];
};

export const truncationPredicates = {
  x: (el) => el?.offsetWidth < el?.scrollWidth,
  y: (el) => el?.offsetHeight < el?.scrollHeight,
};

export const useTruncationTooltip = function useTruncationTooltip(
  props = {},
  axis = 'x'
) {
  return useTooltip(props, truncationPredicates[axis]);
};

export const formatHeaderAndDescriptionToolTip = (header, description) => ({
  html: `<p><b>${header}</b></p><pre>${description}</pre>`,
  description: true,
});
